/**
 * Merge the group members who share the same slot into a single block.
 *
 * In the flat week grid every member contributes their own block. On a
 * 97-member group that turns noon into dozens of stacked slivers, each a few
 * pixels wide, with the titles crushed into an unreadable ribbon. One block
 * carrying a count says the same thing and can actually be read.
 *
 * Events are grouped by start, end, availability code and subject. The API
 * gives us no iCal uid, so that is the closest thing to an identity we have.
 * For blocks that carry a real subject it genuinely is the same appointment;
 * for anonymous free/busy spans it means "these people are all in this state
 * over this slot", which is what the group view is asked for anyway.
 */

/** Local part of an address — everyone here shares the same domain. */
export function shortName(name) {
  return String(name || '').split('@')[0];
}

/** `rgb(...)` for whatever colour shape the API attached to an event. */
function toCss(color) {
  if (Array.isArray(color)) return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
  return typeof color === 'string' && color ? color : '#90a4ae';
}

export function mergeGroupEvents(events, nameByUuid) {
  const buckets = new Map();

  for (const event of events) {
    const key = `${event.start}|${event.end}|${event.fb || ''}|${event.title || ''}`;
    const member = nameByUuid.get(event.memberUuid) || event.memberUuid;
    const bucket = buckets.get(key);
    if (bucket) {
      // Same person twice over one slot (two calendars, say) counts once.
      if (bucket.seen.has(member)) continue;
      bucket.seen.add(member);
      bucket.members.push({ name: member, color: toCss(event.color) });
      continue;
    }
    buckets.set(key, {
      event,
      seen: new Set([member]),
      members: [{ name: member, color: toCss(event.color) }],
    });
  }

  return [...buckets.values()].map(({ event, members }) => {
    const sorted = members.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
    return {
      ...event,
      // A stable id per merged block, so FullCalendar keeps identities across
      // re-renders instead of remounting every block on each fetch.
      id: `merged_${event.start}_${event.end}_${event.fb || ''}`,
      members: sorted.map((m) => m.name),
      // One stripe per member, in the band drawn down the block's left edge.
      memberColors: sorted.map((m) => m.color),
      memberCount: sorted.length,
    };
  });
}

/**
 * Vertical gradient with one hard-edged stripe per colour — no blending, so
 * each member stays a distinct band rather than a smear.
 */
export function buildMemberGradient(colors) {
  if (!colors || colors.length === 0) return 'transparent';
  if (colors.length === 1) return colors[0];
  const stops = [];
  colors.forEach((color, i) => {
    stops.push(`${color} ${(i / colors.length) * 100}%`, `${color} ${((i + 1) / colors.length) * 100}%`);
  });
  return `linear-gradient(to bottom, ${stops.join(', ')})`;
}
